import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { products as productData, ProductItem } from '@/data/products';
import { suppliers as suppliersData } from '@/data/suppliers';
import { useAuthStore } from '@/stores/auth';

// Helper to get auth headers
function getAuthHeaders(): HeadersInit {
  const token = useAuthStore.getState().token;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// Monthly plan interface
export interface MonthlyPlan {
  month: string;
  label: string;
  sensorBoxQty: number;
  controlBoxQty: number;
  smcPoNumber: string;
  supplyDate: string;
  status: 'pending' | 'completed';
}

// Purchase Order interface
export interface PurchaseOrder {
  id: number;
  po_number: string;
  supplier_id: number;
  supplier_name: string;
  total_amount: number;
  status: string;
  payment_status: 'unpaid' | 'paid' | 'partial';
  expected_delivery: string;
  created_at: string;
  line_count?: number;
  notes?: string;
  lines: {
    id: number;
    product_id: number;
    product_name: string;
    sku: string;
    ordered_qty: number;
    received_qty: number;
    remaining_qty: number;
  }[];
}

// Gantt Task interface
export interface GanttTask {
  id: number | string;  // number from DB, string for new items (Date.now())
  taskNumber: number;
  name: string;
  progress: number;
  startDate: string;
  endDate: string;
  daysOfWork: number;
}

// Gantt Order interface
export interface GanttOrder {
  id: number | string;  // number from DB, string for new items (Date.now())
  orderNumber: string;
  productName: string;
  tasks: GanttTask[];
}

// Profit Entry interface
export interface ProfitEntry {
  id: number;
  type: 'revenue' | 'cost';
  category: string;
  description: string;
  amount: number;
  date: string;
  created_at: string;
}

// Work Order interface
export interface WorkOrder {
  id: number;
  woNumber: string;
  poNumber: string;
  product: string;
  qty: number;
  pricePerUnit: number;
  subtotal: number;
  vat: number;
  total: number;
  status: string;
  payment_status?: 'not_received' | 'received';
  orderedDate: string;
  targetEnd: string;
  progress: number;
}

// User interface for admin panel
export interface SystemUser {
  id: number;
  email: string;
  full_name: string;
  role: 'admin' | 'manager' | 'operator' | 'viewer';
  is_active: boolean;
  created_at: string;
  last_login?: string;
}

// Supplier interface
export interface Supplier {
  id: number;
  name: string;
  code: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  payment_terms: string;
  lead_time_days: number;
  notes?: string;
  open_po_count?: number;
  total_open_value?: number;
  is_active?: boolean;
}

// Save status type
export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

// Atomic adjustment result
export interface AdjustmentResult {
  success: boolean;
  updated_values?: Record<number, number>;
  error?: string;
}

// API base URL - will use relative path for Cloudflare Pages
const API_BASE = '/api/v1';

// API response types
interface ApiResponse {
  products?: Array<{
    id: number;
    sku?: string;
    name?: string;
    description?: string;
    manufacturer?: string;
    part_number?: string;
    partNumber?: string;
    parent_id?: number | null;
    parentId?: number | null;
    parent_type?: string | null;
    parentType?: string | null;
    qty_per_system?: number;
    qtyPerSystem?: number;
    start_on_hand?: number;
    startOnHand?: number;
    used?: number;
    unit_cost?: number;
    unitCost?: number;
    type?: string;
  }>;
  inventoryUsed?: Record<string, number>;
  monthlyPlans?: Array<{
    month: string;
    label?: string;
    sensor_box_qty?: number;
    sensorBoxQty?: number;
    control_box_qty?: number;
    controlBoxQty?: number;
    smc_po_number?: string;
    smcPoNumber?: string;
    supply_date?: string;
    supplyDate?: string;
    status?: 'pending' | 'completed';
  }>;
  componentOrders?: Record<string, number>;
  purchaseOrders?: PurchaseOrder[];
  profitEntries?: Array<{
    id: number;
    type: 'revenue' | 'cost';
    category: string;
    description: string;
    amount: number;
    entry_date?: string;
    date?: string;
    created_at: string;
  }>;
  ganttOrders?: Array<{
    id: number | string;
    order_number?: string;
    orderNumber?: string;
    product_name?: string;
    productName?: string;
    tasks?: Array<{
      id: number | string;
      task_number?: number;
      taskNumber?: number;
      name: string;
      progress: number;
      start_date?: string;
      startDate?: string;
      end_date?: string;
      endDate?: string;
      days_of_work?: number;
      daysOfWork?: number;
    }>;
  }>;
  users?: Array<{
    id: number;
    email: string;
    full_name: string;
    role: 'admin' | 'manager' | 'operator' | 'viewer';
    is_active: boolean | number;
    created_at: string;
    last_login?: string;
  }>;
  workOrders?: Array<{
    id: number;
    wo_number?: string;
    woNumber?: string;
    po_number?: string;
    poNumber?: string;
    product: string;
    qty: number;
    price_per_unit?: number;
    pricePerUnit?: number;
    subtotal: number;
    vat: number;
    total: number;
    status: string;
    payment_status?: 'not_received' | 'received';
    ordered_date?: string;
    orderedDate?: string;
    target_end?: string;
    targetEnd?: string;
    progress: number;
  }>;
  suppliers?: Array<{
    id: number;
    name: string;
    code: string;
    contact_name?: string;
    contact_email?: string;
    contact_phone?: string;
    address?: string;
    payment_terms?: string;
    lead_time_days?: number;
    notes?: string;
  }>;
}

// Generate next 6 months
function generateMonths(): MonthlyPlan[] {
  const months: MonthlyPlan[] = [];
  const now = new Date();

  for (let i = 0; i < 6; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const monthKey = date.toISOString().slice(0, 7); // YYYY-MM
    const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    months.push({
      month: monthKey,
      label,
      sensorBoxQty: 0,
      controlBoxQty: 0,
      smcPoNumber: '',
      supplyDate: '',
      status: 'pending',
    });
  }
  return months;
}

// Convert Map to object for serialization
function mapToObject(map: Map<number, number>): Record<number, number> {
  const obj: Record<number, number> = {};
  map.forEach((value, key) => {
    obj[key] = value;
  });
  return obj;
}

interface InventoryContextType {
  products: ProductItem[];
  setProducts: React.Dispatch<React.SetStateAction<ProductItem[]>>;
  monthlyPlan: MonthlyPlan[];
  setMonthlyPlan: React.Dispatch<React.SetStateAction<MonthlyPlan[]>>;
  componentOrders: Map<number, number>;
  setComponentOrders: React.Dispatch<React.SetStateAction<Map<number, number>>>;
  purchaseOrders: PurchaseOrder[];
  setPurchaseOrders: React.Dispatch<React.SetStateAction<PurchaseOrder[]>>;
  profitEntries: ProfitEntry[];
  setProfitEntries: React.Dispatch<React.SetStateAction<ProfitEntry[]>>;
  ganttOrders: GanttOrder[];
  setGanttOrders: React.Dispatch<React.SetStateAction<GanttOrder[]>>;
  workOrders: WorkOrder[];
  setWorkOrders: React.Dispatch<React.SetStateAction<WorkOrder[]>>;
  systemUsers: SystemUser[];
  setSystemUsers: React.Dispatch<React.SetStateAction<SystemUser[]>>;
  // Inventory "Used" tracking - separate from products
  inventoryUsed: Map<number, number>;
  setInventoryUsed: React.Dispatch<React.SetStateAction<Map<number, number>>>;
  saveStatus: SaveStatus;
  isLoading: boolean;
  refreshData: () => Promise<void>;
  // Atomic inventory adjustment - prevents race conditions
  adjustInventory: (productId: number, delta: number) => Promise<AdjustmentResult>;
  adjustInventoryBatch: (adjustments: { product_id: number; delta: number }[]) => Promise<AdjustmentResult>;
  // Instant field updates (no debounce) - use for any inline edits
  updateWorkOrder: (woId: number, updates: Partial<WorkOrder>) => Promise<boolean>;
  updatePurchaseOrder: (poId: number, updates: Partial<PurchaseOrder>) => Promise<boolean>;
  // Work Order CRUD - instant save
  createWorkOrder: (workOrder: Omit<WorkOrder, 'id'> & { id?: number }) => Promise<{ success: boolean; id?: number }>;
  deleteWorkOrder: (woId: number) => Promise<boolean>;
  // Purchase Order CRUD - instant save
  createPurchaseOrder: (po: Omit<PurchaseOrder, 'id' | 'created_at'> & { id?: number }) => Promise<{ success: boolean; id?: number }>;
  deletePurchaseOrder: (poId: number) => Promise<boolean>;
  // Product CRUD - instant save
  createProduct: (product: Omit<ProductItem, 'id'>) => Promise<{ success: boolean; id?: number }>;
  updateProduct: (productId: number, updates: Partial<ProductItem>) => Promise<boolean>;
  deleteProduct: (productId: number) => Promise<boolean>;
  // Supplier CRUD - instant save
  suppliers: Supplier[];
  createSupplier: (supplier: Omit<Supplier, 'id'>) => Promise<{ success: boolean; id?: number }>;
  updateSupplier: (supplierId: number, updates: Partial<Supplier>) => Promise<boolean>;
  deleteSupplier: (supplierId: number) => Promise<boolean>;
}

const InventoryContext = createContext<InventoryContextType | undefined>(undefined);

// Debounce delay in milliseconds (reduced for faster saves)
const AUTOSAVE_DELAY = 500;

export function InventoryProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<ProductItem[]>(productData);
  const [monthlyPlan, setMonthlyPlan] = useState<MonthlyPlan[]>(generateMonths());
  const [componentOrders, setComponentOrders] = useState<Map<number, number>>(new Map());
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [profitEntries, setProfitEntries] = useState<ProfitEntry[]>([]);
  const [ganttOrders, setGanttOrders] = useState<GanttOrder[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  // Separate "Used" tracking for Inventory module (independent from products)
  const [inventoryUsed, setInventoryUsed] = useState<Map<number, number>>(new Map());
  const [systemUsers, setSystemUsers] = useState<SystemUser[]>([
    {
      id: 1,
      email: 'almogc@outlook.com',
      full_name: 'Almog Cohen',
      role: 'admin',
      is_active: true,
      created_at: '2024-01-01T00:00:00Z',
      last_login: new Date().toISOString(),
    },
  ]);
  const [suppliers, setSuppliers] = useState<Supplier[]>(suppliersData.map(s => ({
    id: s.id,
    name: s.name,
    code: s.code,
    contact_name: s.contact_name,
    contact_email: s.contact_email,
    contact_phone: s.contact_phone,
    payment_terms: s.payment_terms || 'Net30',
    lead_time_days: s.lead_time_days || 14,
    open_po_count: s.open_po_count || 0,
    total_open_value: s.total_open_value || 0,
    is_active: s.is_active ?? true,
  })));
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [isLoading, setIsLoading] = useState(true);

  // Track if this is the initial mount (don't save on first load)
  const isInitialMount = useRef(true);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const savedTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Skip autosave when instant save was just performed (prevents redundant full save)
  const skipNextAutosave = useRef(false);
  // Ref to hold latest saveToBackend function (prevents useEffect re-triggering)
  const saveToBackendRef = useRef<(() => Promise<void>) | null>(null);

  // Function to load data from backend
  const loadFromBackend = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE}/app/data`, {
        headers: getAuthHeaders(),
        cache: 'no-store',
      });

      if (!response.ok) {
        // Handle 401 - logout user
        if (response.status === 401) {
          console.log('Session expired, logging out');
          useAuthStore.getState().logout();
          setIsLoading(false);
          return;
        }
        // If API returns 404 or error, use default data (first time setup)
        console.log('No saved data found, using defaults');
        setIsLoading(false);
        return;
      }

      const response_data = await response.json() as { success: boolean; data: ApiResponse };
      const data: ApiResponse = response_data.data || {};

      // Update products - fully replace with backend data if available
      if (data.products && Array.isArray(data.products) && data.products.length > 0) {
        // Backend has products - use them (full replacement)
        setProducts(data.products.map(p => ({
          id: p.id,
          sku: p.sku || '',
          name: p.name || '',
          description: p.description || '',
          manufacturer: p.manufacturer || '',
          partNumber: p.part_number ?? p.partNumber ?? '',
          parentId: p.parent_id ?? p.parentId ?? null,
          parentType: (p.parent_type ?? p.parentType ?? null) as 'sensor_box' | 'control_box' | null,
          qtyPerSystem: p.qty_per_system ?? p.qtyPerSystem ?? 1,
          startOnHand: p.start_on_hand ?? p.startOnHand ?? 0,
          used: p.used ?? 0,
          unitCost: p.unit_cost ?? p.unitCost ?? 0,
          type: (p.type || 'component') as 'assembly' | 'component',
        })));
      }
      // If no products in backend, keep using the initial productData (already set)

      // Update monthly plans
      if (data.monthlyPlans && Array.isArray(data.monthlyPlans)) {
        const savedPlans = data.monthlyPlans;
        const plansMap = new Map(savedPlans.map(p => [p.month, p]));
        setMonthlyPlan(prevPlans => prevPlans.map(plan => {
          const saved = plansMap.get(plan.month);
          if (saved) {
            return {
              ...plan,
              sensorBoxQty: saved.sensor_box_qty ?? saved.sensorBoxQty ?? 0,
              controlBoxQty: saved.control_box_qty ?? saved.controlBoxQty ?? 0,
              smcPoNumber: saved.smc_po_number ?? saved.smcPoNumber ?? '',
              supplyDate: saved.supply_date ?? saved.supplyDate ?? '',
              status: saved.status ?? 'pending',
            };
          }
          return plan;
        }));
      }

      // Update component orders
      if (data.componentOrders) {
        const newMap = new Map<number, number>();
        Object.entries(data.componentOrders).forEach(([key, value]) => {
          newMap.set(Number(key), value);
        });
        setComponentOrders(newMap);
      }

      // Update purchase orders
      if (data.purchaseOrders && Array.isArray(data.purchaseOrders)) {
        setPurchaseOrders(data.purchaseOrders);
      }

      // Update profit entries
      if (data.profitEntries && Array.isArray(data.profitEntries)) {
        setProfitEntries(data.profitEntries.map(e => ({
          id: e.id,
          type: e.type,
          category: e.category,
          description: e.description,
          amount: e.amount,
          date: e.entry_date ?? e.date ?? '',
          created_at: e.created_at,
        })));
      }

      // Update gantt orders
      if (data.ganttOrders && Array.isArray(data.ganttOrders)) {
        setGanttOrders(data.ganttOrders.map(order => ({
          id: order.id,  // Keep as number from DB
          orderNumber: order.order_number ?? order.orderNumber ?? '',
          productName: order.product_name ?? order.productName ?? '',
          tasks: (order.tasks || []).map(task => ({
            id: task.id,  // Keep as number from DB
            taskNumber: task.task_number ?? task.taskNumber ?? 0,
            name: task.name,
            progress: task.progress,
            startDate: task.start_date ?? task.startDate ?? '',
            endDate: task.end_date ?? task.endDate ?? '',
            daysOfWork: task.days_of_work ?? task.daysOfWork ?? 0,
          })),
        })));
      }

      // Update system users
      if (data.users && Array.isArray(data.users)) {
        setSystemUsers(data.users.map(u => ({
          id: u.id,
          email: u.email,
          full_name: u.full_name,
          role: u.role as 'admin' | 'manager' | 'operator' | 'viewer',
          is_active: Boolean(u.is_active),
          created_at: u.created_at,
          last_login: u.last_login,
        })));
      }

      // Update work orders
      if (data.workOrders && Array.isArray(data.workOrders)) {
        setWorkOrders(data.workOrders.map(wo => ({
          id: wo.id,
          woNumber: wo.wo_number ?? wo.woNumber ?? '',
          poNumber: wo.po_number ?? wo.poNumber ?? '',
          product: wo.product,
          qty: wo.qty,
          pricePerUnit: wo.price_per_unit ?? wo.pricePerUnit ?? 0,
          subtotal: wo.subtotal,
          vat: wo.vat,
          total: wo.total,
          status: wo.status,
          payment_status: wo.payment_status || 'not_received',
          orderedDate: wo.ordered_date ?? wo.orderedDate ?? '',
          targetEnd: wo.target_end ?? wo.targetEnd ?? '',
          progress: wo.progress,
        })));
      }

      // Update inventory used (separate from products)
      if (data.inventoryUsed) {
        const newMap = new Map<number, number>();
        Object.entries(data.inventoryUsed).forEach(([key, value]) => {
          newMap.set(Number(key), value);
        });
        setInventoryUsed(newMap);
      }

      // Update suppliers from database
      if (data.suppliers && Array.isArray(data.suppliers) && data.suppliers.length > 0) {
        setSuppliers(data.suppliers.map(s => ({
          id: s.id,
          name: s.name,
          code: s.code,
          contact_name: s.contact_name,
          contact_email: s.contact_email,
          contact_phone: s.contact_phone,
          address: s.address,
          payment_terms: s.payment_terms || 'Net30',
          lead_time_days: s.lead_time_days || 14,
          notes: s.notes,
        })));
      }

      console.log('Data loaded from backend');
    } catch (error) {
      console.error('Failed to load data from backend:', error);
      // Use default data if loading fails
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Get auth state - need to wait for hydration before fetching
  const token = useAuthStore((state) => state.token);
  const hasHydrated = useAuthStore((state) => state._hasHydrated);

  // Load data only AFTER Zustand has hydrated from localStorage AND we have a token
  // This prevents the race condition where data fetch happens before auth state is restored
  useEffect(() => {
    if (hasHydrated && token) {
      loadFromBackend();
    }
  }, [hasHydrated, token, loadFromBackend]);

  // Function to save data to backend
  const saveToBackend = useCallback(async () => {
    setSaveStatus('saving');

    try {
      // Prepare data for saving (transform to API format)
      const dataToSave = {
        products: products.map(p => ({
          id: p.id,
          sku: p.sku,
          name: p.name,
          description: p.description || '',
          manufacturer: p.manufacturer,
          part_number: p.partNumber,
          parent_id: p.parentId,
          parent_type: p.parentType,
          qty_per_system: p.qtyPerSystem,
          start_on_hand: p.startOnHand,
          used: p.used,
          unit_cost: p.unitCost,
          type: p.type,
        })),
        inventoryUsed: mapToObject(inventoryUsed),
        monthlyPlans: monthlyPlan.map(m => ({
          month: m.month,
          label: m.label,
          sensor_box_qty: m.sensorBoxQty,
          control_box_qty: m.controlBoxQty,
          smc_po_number: m.smcPoNumber,
          supply_date: m.supplyDate,
          status: m.status,
        })),
        componentOrders: mapToObject(componentOrders),
        // NOTE: purchaseOrders are EXCLUDED from autosave - they use dedicated CRUD APIs
        profitEntries: profitEntries.map(e => ({
          id: e.id,
          type: e.type,
          category: e.category,
          description: e.description,
          amount: e.amount,
          entry_date: e.date,
        })),
        ganttOrders: ganttOrders.map(order => {
          // Parse ID: if it's a numeric string, convert to number for DB update
          const orderId = typeof order.id === 'string' && /^\d+$/.test(order.id)
            ? parseInt(order.id, 10)
            : order.id;
          return {
            id: orderId,
            order_number: order.orderNumber,
            product_name: order.productName,
            tasks: order.tasks.map(task => {
              const taskId = typeof task.id === 'string' && /^\d+$/.test(task.id)
                ? parseInt(task.id, 10)
                : task.id;
              return {
                id: taskId,
                task_number: task.taskNumber,
                name: task.name,
                progress: task.progress,
                start_date: task.startDate,
                end_date: task.endDate,
                days_of_work: task.daysOfWork,
              };
            }),
          };
        }),
        users: systemUsers.map(u => ({
          id: u.id,
          email: u.email,
          full_name: u.full_name,
          role: u.role,
          is_active: u.is_active,
        })),
        // NOTE: workOrders are EXCLUDED from autosave - they use dedicated CRUD APIs
        // This prevents race conditions where autosave could delete newly created work orders
      };

      const response = await fetch(`${API_BASE}/app/data`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(dataToSave),
      });

      if (!response.ok) {
        throw new Error(`Save failed: ${response.status}`);
      }

      console.log('Autosaved to backend');
      setSaveStatus('saved');

      // Reset to idle after 2 seconds
      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current);
      }
      savedTimeoutRef.current = setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);

    } catch (error) {
      console.error('Failed to save:', error);
      setSaveStatus('error');
    }
  }, [products, monthlyPlan, componentOrders, profitEntries, ganttOrders, systemUsers, inventoryUsed]);

  // Keep ref updated with latest saveToBackend function
  saveToBackendRef.current = saveToBackend;

  // Debounced save effect
  useEffect(() => {
    // Skip saving on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Always clear existing timeout first (prevent stale saves)
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // Skip if instant save was just performed (payment status update)
    // This prevents redundant full save after instant single-field save
    if (skipNextAutosave.current) {
      skipNextAutosave.current = false;
      return;
    }

    // Set new timeout for debounced save
    saveTimeoutRef.current = setTimeout(() => {
      // Use ref to get latest saveToBackend function
      saveToBackendRef.current?.();
    }, AUTOSAVE_DELAY);

    // Cleanup
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
    // Note: saveToBackend intentionally omitted - we use ref to avoid re-triggering
    // Note: workOrders and purchaseOrders are EXCLUDED - they use dedicated instant-save CRUD APIs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, monthlyPlan, componentOrders, profitEntries, ganttOrders, systemUsers, inventoryUsed]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (savedTimeoutRef.current) {
        clearTimeout(savedTimeoutRef.current);
      }
    };
  }, []);

  // Atomic inventory adjustment - single product
  // Uses SQL-level atomic operations to prevent race conditions
  const adjustInventory = useCallback(async (productId: number, delta: number): Promise<AdjustmentResult> => {
    try {
      const response = await fetch(`${API_BASE}/inventory/adjust`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ product_id: productId, delta }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          useAuthStore.getState().logout();
          return { success: false, error: 'Session expired' };
        }
        return { success: false, error: `Adjustment failed: ${response.status}` };
      }

      const result = await response.json() as { success: boolean; data?: { updated_values: Record<number, number> } };

      if (result.success && result.data?.updated_values) {
        // Update local state with server-confirmed values
        setProducts(prev => prev.map(p => {
          const newUsed = result.data!.updated_values[p.id];
          return newUsed !== undefined ? { ...p, used: newUsed } : p;
        }));
        return { success: true, updated_values: result.data.updated_values };
      }

      return { success: false, error: 'Unexpected response format' };
    } catch (error) {
      console.error('Inventory adjustment failed:', error);
      return { success: false, error: 'Network error' };
    }
  }, []);

  // Atomic inventory adjustment - batch of products
  // All adjustments succeed or all fail (transactional)
  const adjustInventoryBatch = useCallback(async (
    adjustments: { product_id: number; delta: number }[]
  ): Promise<AdjustmentResult> => {
    try {
      const response = await fetch(`${API_BASE}/inventory/adjust`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ adjustments }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          useAuthStore.getState().logout();
          return { success: false, error: 'Session expired' };
        }
        return { success: false, error: `Batch adjustment failed: ${response.status}` };
      }

      const result = await response.json() as { success: boolean; data?: { updated_values: Record<number, number> } };

      if (result.success && result.data?.updated_values) {
        // Update local state with server-confirmed values
        setProducts(prev => prev.map(p => {
          const newUsed = result.data!.updated_values[p.id];
          return newUsed !== undefined ? { ...p, used: newUsed } : p;
        }));
        return { success: true, updated_values: result.data.updated_values };
      }

      return { success: false, error: 'Unexpected response format' };
    } catch (error) {
      console.error('Batch inventory adjustment failed:', error);
      return { success: false, error: 'Network error' };
    }
  }, []);

  // Generic instant update for Work Orders - any field(s)
  // Use this for ALL inline edits (status, payment, dates, etc.)
  const updateWorkOrder = useCallback(async (
    woId: number,
    updates: Partial<WorkOrder>
  ): Promise<boolean> => {
    try {
      // Skip the autosave that would be triggered by state change
      skipNextAutosave.current = true;

      // Optimistic update - update local state immediately
      setWorkOrders(prev => prev.map(wo =>
        wo.id === woId ? { ...wo, ...updates } : wo
      ));

      // Convert camelCase to snake_case for API
      const apiPayload: Record<string, unknown> = {};
      if (updates.woNumber !== undefined) apiPayload.wo_number = updates.woNumber;
      if (updates.poNumber !== undefined) apiPayload.po_number = updates.poNumber;
      if (updates.product !== undefined) apiPayload.product = updates.product;
      if (updates.qty !== undefined) apiPayload.qty = updates.qty;
      if (updates.pricePerUnit !== undefined) apiPayload.price_per_unit = updates.pricePerUnit;
      if (updates.subtotal !== undefined) apiPayload.subtotal = updates.subtotal;
      if (updates.vat !== undefined) apiPayload.vat = updates.vat;
      if (updates.total !== undefined) apiPayload.total = updates.total;
      if (updates.status !== undefined) apiPayload.status = updates.status;
      if (updates.payment_status !== undefined) apiPayload.payment_status = updates.payment_status;
      if (updates.orderedDate !== undefined) apiPayload.ordered_date = updates.orderedDate;
      if (updates.targetEnd !== undefined) apiPayload.target_end = updates.targetEnd;
      if (updates.progress !== undefined) apiPayload.progress = updates.progress;

      // Make immediate API call to full update endpoint
      const response = await fetch(`${API_BASE}/work-orders/${woId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(apiPayload),
      });

      if (!response.ok) {
        console.error('Failed to update work order');
        skipNextAutosave.current = true;
        loadFromBackend();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating work order:', error);
      skipNextAutosave.current = true;
      loadFromBackend();
      return false;
    }
  }, [loadFromBackend]);

  // Instant save for creating a new work order
  const createWorkOrder = useCallback(async (
    workOrderData: Omit<WorkOrder, 'id'> & { id?: number }
  ): Promise<{ success: boolean; id?: number }> => {
    try {
      skipNextAutosave.current = true;

      // Generate ID if not provided
      const newId = workOrderData.id || Date.now();

      // Optimistic update - add to local state immediately
      const newWorkOrder: WorkOrder = {
        ...workOrderData,
        id: newId,
      };
      setWorkOrders(prev => [...prev, newWorkOrder]);

      const payload = {
        id: newId,
        wo_number: workOrderData.woNumber,
        po_number: workOrderData.poNumber || '',
        product: workOrderData.product,
        qty: workOrderData.qty,
        price_per_unit: workOrderData.pricePerUnit,
        subtotal: workOrderData.subtotal,
        vat: workOrderData.vat,
        total: workOrderData.total,
        status: workOrderData.status,
        payment_status: workOrderData.payment_status || 'not_received',
        ordered_date: workOrderData.orderedDate || '',
        target_end: workOrderData.targetEnd,
        progress: workOrderData.progress,
      };

      console.log('[createWorkOrder] Sending POST to /api/v1/work-orders with payload:', payload);

      // Make immediate API call
      const response = await fetch(`${API_BASE}/work-orders`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      console.log('[createWorkOrder] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[createWorkOrder] Failed to create work order. Response:', errorText);
        // Revert optimistic update
        setWorkOrders(prev => prev.filter(wo => wo.id !== newId));
        return { success: false };
      }

      const result = await response.json() as { success: boolean; data?: { id: number } };
      console.log('[createWorkOrder] Success! Result:', result);
      return { success: true, id: result.data?.id || newId };
    } catch (error) {
      console.error('[createWorkOrder] Error creating work order:', error);
      return { success: false };
    }
  }, []);

  // Instant delete for work orders
  const deleteWorkOrder = useCallback(async (woId: number): Promise<boolean> => {
    try {
      skipNextAutosave.current = true;

      // Keep a reference for potential rollback
      const deletedWorkOrder = workOrders.find(wo => wo.id === woId);

      // Optimistic update - remove from local state immediately
      setWorkOrders(prev => prev.filter(wo => wo.id !== woId));

      // Make immediate API call
      const response = await fetch(`${API_BASE}/work-orders/${woId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        console.error('Failed to delete work order');
        // Revert optimistic update
        if (deletedWorkOrder) {
          setWorkOrders(prev => [...prev, deletedWorkOrder]);
        }
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error deleting work order:', error);
      return false;
    }
  }, [workOrders]);

  // Generic instant update for Purchase Orders - any field(s)
  // Use this for ALL inline edits (status, payment, supplier, etc.)
  const updatePurchaseOrder = useCallback(async (
    poId: number,
    updates: Partial<PurchaseOrder>
  ): Promise<boolean> => {
    try {
      // Skip the autosave that would be triggered by state change
      skipNextAutosave.current = true;

      // Optimistic update - update local state immediately
      setPurchaseOrders(prev => prev.map(po =>
        po.id === poId ? { ...po, ...updates } : po
      ));

      // Make immediate API call
      const response = await fetch(`${API_BASE}/purchase-orders/${poId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        console.error('Failed to update purchase order');
        skipNextAutosave.current = true;
        loadFromBackend();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating purchase order:', error);
      skipNextAutosave.current = true;
      loadFromBackend();
      return false;
    }
  }, [loadFromBackend]);

  // Instant save for creating a new purchase order
  const createPurchaseOrder = useCallback(async (
    poData: Omit<PurchaseOrder, 'id' | 'created_at'> & { id?: number }
  ): Promise<{ success: boolean; id?: number }> => {
    try {
      skipNextAutosave.current = true;

      // Generate ID if not provided
      const newId = poData.id || Date.now();
      const createdAt = new Date().toISOString();

      // Optimistic update - add to local state immediately
      const newPO: PurchaseOrder = {
        ...poData,
        id: newId,
        created_at: createdAt,
      };
      console.log('[createPurchaseOrder] BEFORE setPurchaseOrders - newPO:', newPO);
      setPurchaseOrders(prev => {
        console.log('[createPurchaseOrder] INSIDE setPurchaseOrders - prev length:', prev.length, 'adding newPO');
        const updated = [...prev, newPO];
        console.log('[createPurchaseOrder] INSIDE setPurchaseOrders - new length:', updated.length);
        return updated;
      });

      const payload = {
        id: newId,
        po_number: poData.po_number,
        supplier_id: poData.supplier_id,
        supplier_name: poData.supplier_name,
        total_amount: poData.total_amount,
        status: poData.status || 'in_progress',
        payment_status: poData.payment_status || 'unpaid',
        expected_delivery: poData.expected_delivery,
        notes: poData.notes || '',
        lines: poData.lines?.map(line => ({
          product_id: line.product_id,
          product_name: line.product_name,
          sku: line.sku,
          ordered_qty: line.ordered_qty,
          received_qty: line.received_qty || 0,
          remaining_qty: line.remaining_qty || line.ordered_qty,
        })) || [],
      };

      console.log('[createPurchaseOrder] Sending POST to /api/v1/purchase-orders with payload:', payload);

      // Make immediate API call
      const response = await fetch(`${API_BASE}/purchase-orders`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      console.log('[createPurchaseOrder] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[createPurchaseOrder] Failed to create purchase order. Response:', errorText);
        // Revert optimistic update
        setPurchaseOrders(prev => prev.filter(po => po.id !== newId));
        return { success: false };
      }

      const result = await response.json() as { success: boolean; data?: { id: number } };
      console.log('[createPurchaseOrder] Success! Result:', result);
      return { success: true, id: result.data?.id || newId };
    } catch (error) {
      console.error('[createPurchaseOrder] Error creating purchase order:', error);
      return { success: false };
    }
  }, []);

  // Instant delete for purchase orders
  const deletePurchaseOrder = useCallback(async (poId: number): Promise<boolean> => {
    try {
      skipNextAutosave.current = true;

      // Keep a reference for potential rollback
      const deletedPO = purchaseOrders.find(po => po.id === poId);

      // Optimistic update - remove from local state immediately
      setPurchaseOrders(prev => prev.filter(po => po.id !== poId));

      // Make immediate API call
      const response = await fetch(`${API_BASE}/purchase-orders/${poId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        console.error('Failed to delete purchase order');
        // Revert optimistic update
        if (deletedPO) {
          setPurchaseOrders(prev => [...prev, deletedPO]);
        }
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error deleting purchase order:', error);
      return false;
    }
  }, [purchaseOrders]);

  // Instant save for creating a new product
  const createProduct = useCallback(async (
    productData: Omit<ProductItem, 'id'>
  ): Promise<{ success: boolean; id?: number }> => {
    try {
      skipNextAutosave.current = true;

      // Make immediate API call
      const response = await fetch(`${API_BASE}/products`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          sku: productData.sku,
          name: productData.name,
          description: productData.description || '',
          manufacturer: productData.manufacturer,
          part_number: productData.partNumber,
          parent_id: productData.parentId,
          parent_type: productData.parentType,
          qty_per_system: productData.qtyPerSystem,
          start_on_hand: productData.startOnHand,
          used: productData.used || 0,
          unit_cost: productData.unitCost,
          type: productData.type,
        }),
      });

      if (!response.ok) {
        console.error('Failed to create product');
        return { success: false };
      }

      const result = await response.json() as { success: boolean; data?: { id: number } };
      const newId = result.data?.id;

      if (newId) {
        // Add to local state with the DB-generated ID
        const newProduct: ProductItem = {
          ...productData,
          id: newId,
        };
        setProducts(prev => [...prev, newProduct]);
        return { success: true, id: newId };
      }

      return { success: false };
    } catch (error) {
      console.error('Error creating product:', error);
      return { success: false };
    }
  }, []);

  // Instant save for updating a product
  const updateProduct = useCallback(async (
    productId: number,
    updates: Partial<ProductItem>
  ): Promise<boolean> => {
    try {
      skipNextAutosave.current = true;

      // Get current product to merge with updates
      const currentProduct = products.find(p => p.id === productId);
      if (!currentProduct) return false;

      const updatedProduct = { ...currentProduct, ...updates };

      // Optimistic update
      setProducts(prev => prev.map(p =>
        p.id === productId ? updatedProduct : p
      ));

      // Make immediate API call
      const response = await fetch(`${API_BASE}/products/${productId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          sku: updatedProduct.sku,
          name: updatedProduct.name,
          description: updatedProduct.description || '',
          manufacturer: updatedProduct.manufacturer,
          part_number: updatedProduct.partNumber,
          parent_id: updatedProduct.parentId,
          parent_type: updatedProduct.parentType,
          qty_per_system: updatedProduct.qtyPerSystem,
          start_on_hand: updatedProduct.startOnHand,
          used: updatedProduct.used || 0,
          unit_cost: updatedProduct.unitCost,
          type: updatedProduct.type,
        }),
      });

      if (!response.ok) {
        console.error('Failed to update product');
        skipNextAutosave.current = true;
        loadFromBackend();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating product:', error);
      skipNextAutosave.current = true;
      loadFromBackend();
      return false;
    }
  }, [products, loadFromBackend]);

  // Instant save for deleting a product
  const deleteProduct = useCallback(async (
    productId: number
  ): Promise<boolean> => {
    try {
      skipNextAutosave.current = true;

      // Optimistic update
      setProducts(prev => prev.filter(p => p.id !== productId));

      // Make immediate API call
      const response = await fetch(`${API_BASE}/products/${productId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        console.error('Failed to delete product');
        skipNextAutosave.current = true;
        loadFromBackend();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error deleting product:', error);
      skipNextAutosave.current = true;
      loadFromBackend();
      return false;
    }
  }, [loadFromBackend]);

  // Instant save for creating a new supplier
  const createSupplier = useCallback(async (
    supplierData: Omit<Supplier, 'id'>
  ): Promise<{ success: boolean; id?: number }> => {
    try {
      skipNextAutosave.current = true;

      const response = await fetch(`${API_BASE}/suppliers`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(supplierData),
      });

      if (!response.ok) {
        console.error('Failed to create supplier');
        return { success: false };
      }

      const result = await response.json() as { success: boolean; data?: { id: number } };
      const newId = result.data?.id;

      if (newId) {
        const newSupplier: Supplier = {
          ...supplierData,
          id: newId,
        };
        setSuppliers(prev => [...prev, newSupplier]);
        return { success: true, id: newId };
      }

      return { success: false };
    } catch (error) {
      console.error('Error creating supplier:', error);
      return { success: false };
    }
  }, []);

  // Instant save for updating a supplier
  const updateSupplier = useCallback(async (
    supplierId: number,
    updates: Partial<Supplier>
  ): Promise<boolean> => {
    try {
      skipNextAutosave.current = true;

      const currentSupplier = suppliers.find(s => s.id === supplierId);
      if (!currentSupplier) return false;

      const updatedSupplier = { ...currentSupplier, ...updates };

      // Optimistic update
      setSuppliers(prev => prev.map(s =>
        s.id === supplierId ? updatedSupplier : s
      ));

      const response = await fetch(`${API_BASE}/suppliers/${supplierId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updatedSupplier),
      });

      if (!response.ok) {
        console.error('Failed to update supplier');
        // Revert on failure
        setSuppliers(prev => prev.map(s =>
          s.id === supplierId ? currentSupplier : s
        ));
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating supplier:', error);
      return false;
    }
  }, [suppliers]);

  // Instant save for deleting a supplier
  const deleteSupplier = useCallback(async (
    supplierId: number
  ): Promise<boolean> => {
    try {
      skipNextAutosave.current = true;

      const deletedSupplier = suppliers.find(s => s.id === supplierId);

      // Optimistic update
      setSuppliers(prev => prev.filter(s => s.id !== supplierId));

      const response = await fetch(`${API_BASE}/suppliers/${supplierId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        console.error('Failed to delete supplier');
        // Revert on failure
        if (deletedSupplier) {
          setSuppliers(prev => [...prev, deletedSupplier]);
        }
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error deleting supplier:', error);
      return false;
    }
  }, [suppliers]);

  return (
    <InventoryContext.Provider
      value={{
        products,
        setProducts,
        monthlyPlan,
        setMonthlyPlan,
        componentOrders,
        setComponentOrders,
        purchaseOrders,
        setPurchaseOrders,
        profitEntries,
        setProfitEntries,
        ganttOrders,
        setGanttOrders,
        workOrders,
        setWorkOrders,
        systemUsers,
        setSystemUsers,
        inventoryUsed,
        setInventoryUsed,
        saveStatus,
        isLoading,
        refreshData: loadFromBackend,
        adjustInventory,
        adjustInventoryBatch,
        updateWorkOrder,
        createWorkOrder,
        deleteWorkOrder,
        updatePurchaseOrder,
        createPurchaseOrder,
        deletePurchaseOrder,
        createProduct,
        updateProduct,
        deleteProduct,
        suppliers,
        createSupplier,
        updateSupplier,
        deleteSupplier,
      }}
    >
      {children}
    </InventoryContext.Provider>
  );
}

export function useInventory() {
  const context = useContext(InventoryContext);
  if (context === undefined) {
    throw new Error('useInventory must be used within an InventoryProvider');
  }
  return context;
}
